import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { ArrowLeft, Wifi, WifiOff, Download, Play, CheckCircle, Loader2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const CACHE_KEY = 'mathtopper_offline_questions';
const CACHE_META_KEY = 'mathtopper_offline_meta';

export default function OfflinePractice() {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [cached, setCached] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [practiceMode, setPracticeMode] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selected, setSelected] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => { window.removeEventListener('online', handleOnline); window.removeEventListener('offline', handleOffline); };
  }, []);

  useEffect(() => {
    const meta = localStorage.getItem(CACHE_META_KEY);
    const qs = localStorage.getItem(CACHE_KEY);
    if (meta && qs) {
      setCached({ meta: JSON.parse(meta), questions: JSON.parse(qs) });
    }
  }, []);

  const downloadPack = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/student/offline-pack`, { headers, withCredentials: true });
      const data = res.data;
      localStorage.setItem(CACHE_KEY, JSON.stringify(data.questions));
      localStorage.setItem(CACHE_META_KEY, JSON.stringify({
        total: data.total, class_level: data.class_level,
        chapters: data.chapters, cached_at: data.cached_at,
      }));
      setCached({ meta: { total: data.total, class_level: data.class_level, chapters: data.chapters, cached_at: data.cached_at }, questions: data.questions });
      toast.success(`${data.total} questions saved for offline practice!`);
    } catch {
      toast.error('Failed to download. Check your connection.');
    } finally {
      setDownloading(false);
    }
  };

  const startPractice = () => {
    if (!cached?.questions?.length) { toast.error('No cached questions'); return; }
    const shuffled = [...cached.questions].sort(() => Math.random() - 0.5);
    setCached(prev => ({ ...prev, questions: shuffled }));
    setPracticeMode(true);
    setCurrentIdx(0);
    setScore({ correct: 0, total: 0 });
    setSelected('');
    setShowAnswer(false);
  };

  const handleAnswer = (option) => {
    if (showAnswer) return;
    setSelected(option);
    setShowAnswer(true);
    const q = cached.questions[currentIdx];
    const isCorrect = option === q.correct_option;
    setScore(prev => ({ correct: prev.correct + (isCorrect ? 1 : 0), total: prev.total + 1 }));
  };

  const nextQuestion = () => {
    if (currentIdx + 1 >= cached.questions.length) {
      setPracticeMode(false);
      toast.success(`Practice complete! Score: ${score.correct}/${score.total}`);
      return;
    }
    setCurrentIdx(prev => prev + 1);
    setSelected('');
    setShowAnswer(false);
  };

  if (practiceMode && cached?.questions?.length > 0) {
    const q = cached.questions[currentIdx];
    const options = ['a', 'b', 'c', 'd'];
    return (
      <div className="min-h-screen pb-20" style={{ background: '#FFF8F0' }}>
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <button onClick={() => setPracticeMode(false)} data-testid="exit-offline-practice">
            <ArrowLeft className="w-5 h-5 text-[#1B4F8A]" />
          </button>
          <div className="flex items-center gap-2">
            <WifiOff size={14} className="text-[#E63946]" />
            <span className="text-xs font-bold text-[#1B4F8A]">Offline Mode</span>
          </div>
          <span className="text-xs font-bold text-[#8B7355]">{currentIdx + 1}/{Math.min(cached.questions.length, 20)}</span>
        </div>

        <div className="px-4 space-y-3">
          <div className="bg-[#FF6B35]/10 rounded-full h-2 overflow-hidden">
            <div className="bg-[#FF6B35] h-full transition-all rounded-full" style={{ width: `${((currentIdx + 1) / Math.min(cached.questions.length, 20)) * 100}%` }} />
          </div>

          <div className="flex gap-2 text-xs">
            <span className="px-2 py-1 bg-[#EDE9FE] text-[#8338EC] rounded-full font-bold">{q.chapter?.replace(/_/g, ' ')}</span>
            <span className="px-2 py-1 bg-[#FFF0E5] text-[#FF6B35] rounded-full font-bold">{q.difficulty || 'Medium'}</span>
          </div>

          <div className="bg-white border-2 rounded-2xl p-4" style={{ borderColor: '#E5DACE' }}>
            <p className="text-sm font-bold text-[#1B4F8A] leading-relaxed" data-testid="offline-question-text">{q.question_text}</p>
          </div>

          <div className="space-y-2">
            {options.map(opt => {
              const optText = q[`option_${opt}`];
              if (!optText) return null;
              const isCorrect = q.correct_option === opt;
              const isSelected = selected === opt;
              let style = { borderColor: '#E5DACE', background: 'white' };
              if (showAnswer) {
                if (isCorrect) style = { borderColor: '#06D6A0', background: '#E5FFF5' };
                else if (isSelected && !isCorrect) style = { borderColor: '#E63946', background: '#FFF0F0' };
              } else if (isSelected) {
                style = { borderColor: '#8338EC', background: '#F3F0FF' };
              }
              return (
                <button key={opt} onClick={() => handleAnswer(opt)} data-testid={`offline-option-${opt}`}
                  className="w-full text-left border-2 rounded-xl p-3 flex items-center gap-3 transition-all"
                  style={style} disabled={showAnswer}>
                  <span className="w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold shrink-0"
                    style={{ borderColor: style.borderColor, color: '#1B4F8A' }}>
                    {opt.toUpperCase()}
                  </span>
                  <span className="text-sm font-semibold text-[#1B4F8A]">{optText}</span>
                  {showAnswer && isCorrect && <CheckCircle size={16} className="ml-auto text-[#06D6A0]" />}
                </button>
              );
            })}
          </div>

          {showAnswer && (
            <div className="space-y-2">
              {q.solution && (
                <div className="bg-[#F0E6D8] rounded-xl p-3">
                  <p className="text-xs font-bold text-[#8B7355] mb-1">Solution</p>
                  <p className="text-xs text-[#1B4F8A]">{q.solution}</p>
                </div>
              )}
              <Button onClick={nextQuestion} data-testid="offline-next-btn"
                className="w-full bg-[#FF6B35] text-white font-bold rounded-full py-3">
                {currentIdx + 1 >= Math.min(cached.questions.length, 20) ? 'Finish Practice' : 'Next Question'}
              </Button>
            </div>
          )}

          <div className="flex justify-between text-xs font-bold px-2">
            <span className="text-[#06D6A0]">Correct: {score.correct}</span>
            <span className="text-[#E63946]">Wrong: {score.total - score.correct}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{ background: '#FFF8F0' }}>
      <div className="px-4 pt-4 pb-2 flex items-center gap-3">
        <button onClick={() => navigate('/home')} data-testid="back-from-offline">
          <ArrowLeft className="w-5 h-5 text-[#1B4F8A]" />
        </button>
        <h1 className="text-lg font-extrabold text-[#1B4F8A]">Offline Practice</h1>
      </div>

      <div className="px-4 space-y-4">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold"
          style={{ background: isOnline ? '#E5FFF5' : '#FFF0E5', color: isOnline ? '#06D6A0' : '#E63946' }}>
          {isOnline ? <Wifi size={14} /> : <WifiOff size={14} />}
          {isOnline ? 'You are online' : 'You are offline'}
        </div>

        {cached ? (
          <div className="bg-white border-2 border-[#06D6A0] rounded-2xl p-5 space-y-3" data-testid="offline-cache-info">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-[#06D6A0]" />
              <span className="text-sm font-bold text-[#1B4F8A]">{cached.meta.total} Questions Cached</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-[#F8F4EF] rounded-lg p-2">
                <span className="font-bold text-[#8B7355]">Class:</span> <span className="text-[#1B4F8A] font-bold">{cached.meta.class_level}</span>
              </div>
              <div className="bg-[#F8F4EF] rounded-lg p-2">
                <span className="font-bold text-[#8B7355]">Chapters:</span> <span className="text-[#1B4F8A] font-bold">{cached.meta.chapters?.length || 0}</span>
              </div>
            </div>
            <p className="text-[10px] text-slate-400">Last updated: {new Date(cached.meta.cached_at).toLocaleString()}</p>
            <div className="flex gap-2">
              <Button onClick={startPractice} data-testid="start-offline-practice"
                className="flex-1 bg-[#FF6B35] text-white font-bold rounded-full">
                <Play size={14} className="mr-1" /> Practice Now
              </Button>
              {isOnline && (
                <Button onClick={downloadPack} disabled={downloading} variant="outline"
                  data-testid="refresh-offline-pack"
                  className="font-bold rounded-full border-[#8338EC] text-[#8338EC]">
                  {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white border-2 border-dashed border-[#E5DACE] rounded-2xl p-6 text-center space-y-3" data-testid="offline-empty">
            <BookOpen size={40} className="mx-auto text-[#E5DACE]" />
            <p className="text-sm font-bold text-[#1B4F8A]">No questions cached yet</p>
            <p className="text-xs text-[#8B7355]">Download 100 questions to practice anytime, even without internet!</p>
            <Button onClick={downloadPack} disabled={downloading || !isOnline}
              data-testid="download-offline-pack"
              className="bg-[#8338EC] text-white font-bold rounded-full px-6">
              {downloading ? <><Loader2 size={14} className="animate-spin mr-1" /> Downloading...</> :
                <><Download size={14} className="mr-1" /> Download for Offline</>}
            </Button>
            {!isOnline && <p className="text-xs text-[#E63946] font-bold">Connect to internet to download</p>}
          </div>
        )}

        <div className="bg-[#F8F4EF] rounded-2xl p-4 space-y-2">
          <p className="text-xs font-bold text-[#8B7355]">How Offline Practice Works</p>
          <div className="space-y-1.5 text-xs text-[#1B4F8A]">
            <p className="flex items-start gap-2"><Download size={12} className="text-[#8338EC] shrink-0 mt-0.5" /> Download 100 questions from your class syllabus</p>
            <p className="flex items-start gap-2"><WifiOff size={12} className="text-[#E63946] shrink-0 mt-0.5" /> Practice anytime — no internet needed</p>
            <p className="flex items-start gap-2"><CheckCircle size={12} className="text-[#06D6A0] shrink-0 mt-0.5" /> Get instant answers and solutions</p>
          </div>
        </div>
      </div>
      <BottomNav active="home" />
    </div>
  );
}
